import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, ChevronRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import DeleteSessionButton from "@/components/DeleteSessionButton";
import { api_listMySessions, api_getMyProfile } from "@/lib/api";

export default function History() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api_listMySessions(), api_getMyProfile()]);
      setSessions(s);
      setProfile(p);
    } catch (e) {
      toast.error("Could not load history.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell profile={profile}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Your journey</p>
      <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Practice history</h1>
      <p className="mt-2 text-slate-600">Every session you've finished, tracked, and learned from.</p>

      <div className="mt-8 space-y-3">
        {sessions.length === 0 && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">
            No sessions yet. Build one from the dashboard.
          </div>
        )}
        {sessions.map((s) => {
          const outs = Object.values(s.feedback || {}).flat();
          const rate = outs.length ? Math.round((outs.filter((o) => o === "success").length / outs.length) * 100) : 0;
          const go = () => navigate(s.status === "completed" ? `/reflection/${s.id}` : `/session/${s.id}`);
          return (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              data-testid={`history-item-${s.id}`}
              onClick={go}
              onKeyDown={(e) => { if (e.key === "Enter") go(); }}
              className="w-full text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 hover:-translate-y-0.5 transition-all flex items-center justify-between gap-3 cursor-pointer"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                    {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${s.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {s.status}
                  </span>
                </div>
                <p className="mt-2 font-heading font-bold text-lg leading-tight truncate">{s.title}</p>
                <div className="mt-2 flex items-center gap-3 text-xs font-bold text-slate-600">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {s.time_minutes}m</span>
                  <span>{s.drills.length} drills</span>
                  {s.status === "completed" && <span>{rate}% success</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <DeleteSessionButton sessionId={s.id} onDeleted={load} />
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
